import React, { useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';
import styled from 'styled-components';
import colors from 'theme/colors';
import { ChevronLeft, ChevronRight, Clock, Star } from 'lucide-react';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import { placeIsOpen, placeRating, placeSummary, PLACEHOLDER_EVENT } from 'theme/discover';
import {
    HeroCarouselWrapper,
    HeroCarouselViewport,
    heroSwiperSkin,
    heroBreakpoints,
    HeroArrow,
    HeroCard,
    HeroScrim,
    HeroCorner,
    HeroBadge,
    HeroContent,
    HeroTitle,
    HeroSubtitle,
    HeroMeta,
} from 'components/commons/HeroCard';

/**
 * Lugares em destaque no desenho do app (WUIHeroCard com o selo "Em
 * destaque"): foto inteira, logo do lugar no canto, nome, categoria e bairro,
 * se está aberto e a nota.
 */
const EstablishmentCarousel = ({ data, onSlideClick }) => {
    const swiperRef = useRef(null);

    // Mesmas regras da faixa de eventos: sem autoplay para quem pediu menos
    // movimento, e o modo giratório só com cartões suficientes para girar.
    const prefersReducedMotion = typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const canLoop = data?.length >= 6;

    const handleNext = () => swiperRef.current?.slideNext();
    const handlePrev = () => swiperRef.current?.slidePrev();

    if (!data?.length) return null;

    return (
        <HeroCarouselWrapper>
            <HeroArrow $side='left' onClick={handlePrev} aria-label='Destaque anterior'>
                <ChevronLeft size={18} />
            </HeroArrow>
            <HeroArrow $side='right' onClick={handleNext} aria-label='Próximo destaque'>
                <ChevronRight size={18} />
            </HeroArrow>

            <HeroCarouselViewport>
                <StyledSwiper
                    $emphasis={canLoop}
                    loop={canLoop}
                    breakpoints={heroBreakpoints(canLoop)}
                    autoplay={prefersReducedMotion ? false : {
                        delay: 6000,
                        disableOnInteraction: true,
                        pauseOnMouseEnter: true,
                    }}
                    pagination={{ clickable: true }}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    modules={[Autoplay, Pagination]}
                >
                    {data.map((item) => (
                        <SwiperSlide key={item.slug} onClick={() => onSlideClick?.(item)}>
                            <PlaceHeroCard item={item} />
                        </SwiperSlide>
                    ))}
                </StyledSwiper>
            </HeroCarouselViewport>
        </HeroCarouselWrapper>
    );
};

function PlaceHeroCard({ item }) {
    const isOpen = placeIsOpen(item);
    const rating = placeRating(item);
    const summary = placeSummary(item);

    return (
        <HeroCard>
            <img
                src={item?.backgroundImage?.path || PLACEHOLDER_EVENT}
                alt={item?.name}
                onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_EVENT; }}
            />
            <HeroScrim />

            <HeroCorner $side='left'>
                <HeroBadge>
                    <Star size={12} />
                    Em destaque
                </HeroBadge>
            </HeroCorner>

            <HeroCorner $side='right'>
                <Logo>
                    <DynamicProfileImage
                        img={item?.profileImage?.thumb || item?.profileImage?.path}
                        word={item?.name}
                        size='40px'
                        textSize='normal'
                    />
                </Logo>
            </HeroCorner>

            <HeroContent>
                <HeroTitle>{item?.name}</HeroTitle>
                {!!summary && <HeroSubtitle>{summary}</HeroSubtitle>}
                <HeroMeta>
                    <span>
                        <Clock size={12} />
                        {isOpen ? 'Aberto agora' : 'Fechado'}
                    </span>
                    {!!rating && (
                        <span>
                            <Star size={12} fill='currentColor' />
                            {rating}
                        </span>
                    )}
                </HeroMeta>
            </HeroContent>
        </HeroCard>
    );
}

const StyledSwiper = styled(Swiper)`
    ${heroSwiperSkin}
`;

const Logo = styled.span`
    display: flex;
    border-radius: 50%;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85), 0 4px 12px rgba(10, 5, 25, 0.3);
    background: ${colors.neutralLight};
`;

export default EstablishmentCarousel;
